import { chipById, type ChipId } from './chips';
import { lookFromPrompt } from './looks';

/**
 * What a signed-out visitor sent from the landing page, kept in this browser so it survives a Google redirect, an
 * email verification and a closed tab. Sending it also records it on our server (capture.ts); nothing here joins the
 * two, so this draft holds no id.
 */
export interface DraftLook {
  /** A design-pack id, or null for Ivy to choose. */
  id: string | null;
  from: 'picked' | 'words';
}

export interface DraftFields {
  prompt: string;
  look: DraftLook | null;
  /** The landing page idea it came from, while its words are unchanged. */
  chip: ChipId | null;
  /** When they pressed Create. Only a draft armed in the last 30 minutes starts by itself. */
  armedAt: number | null;
  /** It came by a link we can't vouch for: it waits for the person to press Start, even after sign-in. */
  askFirst?: boolean;
  /** The notice was showing when they sent: only then is anything about this draft recorded, sign-in and the claim included. */
  noticed?: boolean;
}

export interface Draft extends DraftFields {
  savedAt: number;
}

const KEY = 'ivy.frontDoor.draft';
const CLAIM_KEY = 'ivy.frontDoor.claim';
const VERSION = 2;
const KEEP_MS = 7 * 24 * 60 * 60_000;
const AUTO_START_MS = 30 * 60_000;
const CLAIM_MS = 2 * 60_000;

/** No browser storage (private window, blocked site data): the draft lives in the page only. */
const inPage = new Map<string, unknown>();

function read(key: string): unknown {
  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return inPage.get(key) ?? null;
  }
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    if (value === null) inPage.delete(key);
    else inPage.set(key, value);
  }
}

export function saveDraft(fields: DraftFields, now = Date.now()): void {
  write(KEY, fields.prompt.trim() ? { v: VERSION, ...fields, savedAt: now } : null);
}

export function readDraft(now = Date.now()): Draft | null {
  const stored = read(KEY) as (Draft & { v?: number }) | null;
  if (!stored || stored.v !== VERSION || typeof stored.prompt !== 'string' || typeof stored.savedAt !== 'number') return null;
  if (now - stored.savedAt > KEEP_MS) {
    write(KEY, null);
    return null;
  }
  const draft = { ...stored };
  delete draft.v;
  return draft;
}

export const autoStarts = (draft: Draft, now = Date.now()): boolean =>
  draft.armedAt !== null && !draft.askFirst && now - draft.armedAt <= AUTO_START_MS;

/** Only one tab may start the draft, so a video is never created twice. */
export function claimDraft(tab: string, now = Date.now()): boolean {
  const claim = read(CLAIM_KEY) as { tab?: string; at?: number } | null;
  if (claim?.tab && claim.tab !== tab && typeof claim.at === 'number' && now - claim.at < CLAIM_MS) return false;
  write(CLAIM_KEY, { tab, at: now });
  return true;
}

export function clearDraft(): void {
  write(KEY, null);
  write(CLAIM_KEY, null);
}

/** The draft for words sent from the landing page: the look the words name, if any. An idea's words name none, so Ivy picks it. */
export function draftFromWords(prompt: string, chipId: unknown, now = Date.now()): DraftFields {
  const chip = chipById(chipId);
  const fromChip = chip && chip.prompt === prompt ? chip : null;
  const named = lookFromPrompt(prompt);
  const look: DraftLook | null = named?.kind === 'look' ? { id: named.id, from: 'words' } : named ? { id: null, from: 'words' } : null;
  return { prompt, look, chip: fromChip?.id ?? null, armedAt: now };
}
